import { ChevronLeft, ChevronRight } from "lucide-react";
import { type Ref, useId } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { AccountMenu } from "@/components/account-menu";
import { BrandLogo } from "@/components/brand-logo";
import { EnvironmentBadge } from "@/components/environment-badge";
import { RailTooltip } from "@/components/rail-tooltip";
import { Separator } from "@/components/ui/separator";
import { useApps, useConnectorOverview, useMe } from "@/lib/api";
import { APP_NAME } from "@/lib/app-info";
import {
  appsNavGroup,
  DOCS_ITEM,
  isNavItemCurrent,
  type NavGroup,
  type NavItem,
  visibleNavGroups,
  withAppsGroup,
} from "@/lib/navigation";
import { isAdminRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

interface AppSidebarProps {
  /** Runs on every link and menu choice; the phone's sheet and the panel over the page close on it. */
  onNavigate?: () => void;
  /**
   * The sidebar as a narrow rail: the logo mark and the icons stay, the labels
   * hide and come back as tooltips.
   */
  collapsed?: boolean;
  /** The sidebar's id, which the edge handle beside it names as what it controls. */
  id?: string;
}

/**
 * The sidebar's content, the same beside the page, over it and in the phone's
 * sheet: the logo, outside production the environment's badge under it
 * ("Dev", "Alleen lezen"), the connector's pages (Mensen en teams, the
 * Roadmap and Analytics for admins only), under them the apps the person may
 * open (each with its unread meldingen; the group is absent while there are
 * none or the list is loading), and a pinned footer with Docs and the account
 * menu, which leads to Settings (where Account, the activity log and About
 * live). The connector's
 * status is on Home, not here. Outside the phone's sheet the shell puts
 * `<SidebarEdgeToggle>` on the sidebar's right border, level with the logo.
 * Collapsing moves nothing: the badge is as tall in both forms, so the header
 * keeps its height and every icon keeps its place, only the labels hide. The
 * navigation scrolls on its own when the window is short; the whole sidebar
 * scrolls on a very short one, so the footer stays reachable.
 */
export function AppSidebar({ onNavigate, collapsed = false, id }: AppSidebarProps) {
  const { data: me } = useMe();
  // The query every connector page reads too: one cached request for all.
  const { data: overview } = useConnectorOverview();
  // Only the apps the person may open: someone without access never sees one.
  const { data: apps } = useApps();
  // Re-renders the sidebar on a language switch: the labels are read at render.
  const { t } = useTranslation();
  const groups = withAppsGroup(visibleNavGroups(isAdminRole(me?.role)), appsNavGroup(apps?.apps));

  return (
    <div
      id={id}
      className="bg-sidebar text-sidebar-foreground scrollbar-themed flex h-full flex-col overflow-x-hidden overflow-y-auto"
    >
      {/* The logo's row: `SidebarEdgeToggle`'s `top-6` centres the handle on it. */}
      <div className={cn("flex flex-col gap-3 pt-5 pb-4", collapsed ? "items-center" : "px-5")}>
        <Link
          to="/home"
          onClick={onNavigate}
          aria-label={t("shell.homeLink", { app: APP_NAME })}
          className={cn(
            "focus-visible:ring-ring rounded-sm focus-visible:ring-2 focus-visible:outline-none",
            !collapsed && "self-start",
          )}
        >
          <BrandLogo variant={collapsed ? "mark" : "compact"} className="h-8" />
        </Link>
        <EnvironmentBadge
          overview={overview}
          compact={collapsed}
          tooltipSide={collapsed ? "right" : "bottom"}
          className={collapsed ? undefined : "self-start"}
        />
      </div>
      <Separator className="bg-sidebar-border" />
      <nav
        aria-label={t("nav.main")}
        className="scrollbar-themed min-h-24 flex-1 overflow-x-hidden overflow-y-auto px-3 py-4"
      >
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <SidebarGroup
              key={group.items[0]?.to}
              group={group}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </nav>
      <div className="border-sidebar-border flex flex-col gap-1 border-t px-3 pt-3 pb-4">
        <SidebarLink item={DOCS_ITEM} collapsed={collapsed} onNavigate={onNavigate} />
        <AccountMenu collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

interface SidebarGroupProps {
  group: NavGroup;
  collapsed: boolean;
  onNavigate?: () => void;
}

/**
 * A group of pages under its small label, which names the list for screen
 * readers. In the rail the label keeps its place but is not shown, so the
 * icons stay where they are.
 */
function SidebarGroup({ group, collapsed, onNavigate }: SidebarGroupProps) {
  const labelId = useId();
  return (
    <div className="flex flex-col gap-0.5">
      <p
        id={labelId}
        className={cn(
          "text-sidebar-muted truncate px-3 pb-1 text-xs font-semibold",
          collapsed && "invisible",
        )}
      >
        {group.label}
      </p>
      <ul aria-labelledby={labelId} className="flex flex-col gap-0.5">
        {group.items.map((item) => (
          <li key={item.to}>
            <SidebarLink item={item} collapsed={collapsed} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SidebarLinkProps {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}

/**
 * A page in the sidebar; the current one carries `aria-current="page"` and the
 * blue tint, in the rail too. In the rail its label is screen-reader text and
 * a tooltip; the row keeps the height of a line of text (`min-h-9`), so the
 * icons stay where they are. A badge (an app's unread meldingen) sits at the
 * row's end; in the rail it is a dot on the icon, and the count is in the
 * tooltip. Either way the link's name says what it counts.
 */
function SidebarLink({ item, collapsed, onNavigate }: SidebarLinkProps) {
  const { t } = useTranslation();
  // Prefix matching, so a page's own sub-routes (every page of the docs) keep it current.
  const active = isNavItemCurrent(item, useLocation().pathname);
  const Icon = item.icon;
  const badge = item.badge ?? null;
  const counted = badge && item.badgeLabel ? item.badgeLabel : null;
  return (
    <RailTooltip
      label={counted ? t("apps.linkWithUnread", { app: item.label, unread: counted }) : item.label}
      show={collapsed}
    >
      <Link
        to={item.to}
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        className={cn(
          "focus-visible:ring-ring flex min-h-9 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none",
          active
            ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        )}
      >
        {badge && collapsed ? (
          <span className="relative shrink-0">
            <Icon className="size-4" aria-hidden="true" />
            <span
              aria-hidden="true"
              data-badge="dot"
              className="bg-primary ring-sidebar absolute -top-1 -right-1 size-2 rounded-full ring-2"
            />
          </span>
        ) : (
          <Icon className="size-4 shrink-0" aria-hidden="true" />
        )}
        <span className={collapsed ? "sr-only" : cn("truncate", badge && "min-w-0 flex-1")}>
          {item.label}
        </span>
        {badge && !collapsed && (
          <span
            aria-hidden="true"
            data-badge="count"
            className="bg-primary text-primary-foreground min-w-5 shrink-0 rounded-full px-1.5 text-center text-xs leading-5 font-semibold"
          >
            {badge}
          </span>
        )}
        {counted && <span className="sr-only">, {counted}</span>}
      </Link>
    </RailTooltip>
  );
}

interface SidebarEdgeToggleProps {
  /** Whether the sidebar shows as the rail: the handle then expands it. */
  collapsed: boolean;
  onToggle: () => void;
  /** The sidebar's id (`AppSidebar`'s `id`): the handle expands and collapses it. */
  controls: string;
  /** So the panel over the page can hand focus back to the handle when it closes. */
  buttonRef?: Ref<HTMLButtonElement>;
  /**
   * Whether its label shows as a tooltip. Not in the open panel over the page:
   * an open tooltip would take the Escape that closes the panel
   * (`useOutsideDismiss`), so one Escape would no longer do.
   */
  tooltip: boolean;
}

/**
 * The handle that collapses the sidebar to a rail of icons and expands it
 * again: a small round button straddling the sidebar's right border, its
 * centre on the border line, level with the logo. A chevron points the way
 * the sidebar will go: left to collapse it, right to expand it. Quiet at rest
 * (the sidebar's fill, a hairline border, a muted chevron), blue on hover and
 * on keyboard focus.
 *
 * The shell places it in the element that carries the border, before the
 * sidebar itself: outside the sidebar's scroll container, which clips
 * sideways and would cut the half that overhangs the page, and first in the
 * tab order, so Tab from it walks into the open panel instead of out of it
 * (which would close the panel). As it is positioned on that element's right
 * edge, it follows the edge as it moves: the full sidebar's, the rail's, the
 * panel's over the page, also while the width animates. Beside the page its
 * overhang, half of its 1.5rem, stays inside the page's 1.5rem gutter
 * (`PAGE_GUTTER`), so it covers nothing there. `top-6` centres it on the
 * logo's row: the header's 1.25rem top padding plus half the 2rem logo, less
 * half the handle.
 *
 * Its label is screen-reader text and a tooltip; on a touch screen, where
 * Radix tooltips never show, its tap area grows to 44 px (`tap-target`).
 * Centred on the handle, that area would reach 4.5 px into the logo mark in
 * the rail, so a tap on the logo's right edge would toggle the menu instead
 * of going Home. It is shifted 5 px to the right (`--tap-target-shift`): in
 * the rail it then starts just past the logo mark and ends at the page's
 * gutter, and beside the full sidebar it ends at the gutter too.
 */
export function SidebarEdgeToggle({
  collapsed,
  onToggle,
  controls,
  buttonRef,
  tooltip,
}: SidebarEdgeToggleProps) {
  const { t } = useTranslation();
  const label = collapsed ? t("shell.expandMenu") : t("shell.collapseMenu");
  const Icon = collapsed ? ChevronRight : ChevronLeft;
  return (
    <RailTooltip label={label} show={tooltip}>
      <button
        ref={buttonRef}
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={controls}
        className="bg-sidebar border-sidebar-border text-sidebar-muted hover:border-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:border-sidebar-accent-foreground focus-visible:text-sidebar-accent-foreground focus-visible:ring-ring tap-target absolute top-6 right-0 z-10 flex size-6 translate-x-1/2 items-center justify-center rounded-full border transition-colors [--tap-target-shift:5px] focus-visible:ring-2 focus-visible:outline-none"
      >
        <Icon className="size-4" aria-hidden="true" />
        <span className="sr-only">{label}</span>
      </button>
    </RailTooltip>
  );
}
